/**
 * Resolves which questions are currently visible by walking the branching
 * flow from the first question via each question's `next_question_name`.
 *
 * @module pharmaQuestionaire/utils/visibleQuestionChain
 */
import { QuestionForUI } from "../../../data-model/cmmMedicalNeccessityQuestionnaire";

/**
 * Walks the question flow from the first question, following each
 * question's `next_question_name`, and returns the chain of questions
 * the user can currently see.
 *
 * Cycles are guarded with a `seen` set so a misconfigured flow can't
 * loop forever.
 *
 * @param questions - All transformed questions in source order
 * @returns Ordered chain of visible questions starting at `questions[0]`
 */
export function getVisibleQuestionChain(
  questions: QuestionForUI[],
): QuestionForUI[] {
  if (questions.length === 0) return [];
  const byName = new Map(questions.map((q) => [q.name, q]));
  const visible: QuestionForUI[] = [];
  const seen = new Set<string>();
  let current: QuestionForUI | undefined = questions[0];
  while (current && !seen.has(current.name)) {
    visible.push(current);
    seen.add(current.name);
    const next = current.next_question_name;
    current = next ? byName.get(next) : undefined;
  }
  return visible;
}
